import React from 'react'
import { Box, Paper, Typography, BreakpointOverrides } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import NavigationGroupLink from '../NavigationGroup/NavigationGroupLink'
import Image from '../ContentfulEntries/Image'
import { AssetFluid } from '../../types/asset'
import {
  IGenericGrid,
  getOverridenBreakpointKeysObject,
  toTwoDecimalPlaces,
} from '../../types/grid'

// eslint-disable-next-line @typescript-eslint/naming-convention
const COLUMN_MAX_WIDTH = 100

export function GenericGrid({
  gridCells,
  desktop,
  laptop,
  tablet,
  mobile,
  spacing = 0,
}: IGenericGrid) {
  const { classes, cx } = useStyles({
    columnsPerScreen: { desktop, laptop, tablet, mobile },
  })

  return (
    <Box className={classes.container} sx={{ gap: spacing }}>
      {gridCells.map((item, i) => (
        <Box className={classes.gridItem} key={item.title + i}>
          {item.link ? (
            <NavigationGroupLink link={item.link}>
              <Paper
                variant="outlined"
                className={cx(classes.paper, classes.link)}
              >
                <Image asset={item.cellIcon as AssetFluid} />
                <Typography className={classes.cellText}>
                  {item.link.text}
                </Typography>
              </Paper>
            </NavigationGroupLink>
          ) : (
            <Paper variant="outlined" className={classes.paper}>
              <Image asset={item.cellIcon} />
              <Typography className={classes.cellText}>{item.title}</Typography>
            </Paper>
          )}
        </Box>
      ))}
    </Box>
  )
}

const useStyles = makeStyles<{ columnsPerScreen: any }>()(
  (theme, { columnsPerScreen }) => {
    const breakpoints = getOverridenBreakpointKeysObject(
      theme.breakpoints.values
    )
    const gridItem = Object.keys(breakpoints).reduce((acc, val) => {
      acc[theme.breakpoints.up(val as keyof BreakpointOverrides)] = {
        '&&': {
          flexBasis:
            toTwoDecimalPlaces(
              COLUMN_MAX_WIDTH /
                columnsPerScreen[val as keyof BreakpointOverrides]
            ) + '%',
        },
      }
      return acc
    }, {} as any)

    return {
      root: {
        flexGrow: 1,
      },
      paper: {
        padding: theme.spacing(1.25),
        color: theme.palette.text.primary,
        backgroundColor: theme.colors.white[300],
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        justifyItems: 'start',
        gap: theme.spacing(1.5),
        textTransform: 'capitalize',
        height: '100%',
      },
      container: {
        display: 'flex',
        flexWrap: 'wrap',
        paddingInline: theme.spacing(2),
        paddingTop: theme.spacing(1),
      },
      link: {
        '&:hover': {
          color: theme.colors.blue['a400'],
        },
      },
      gridItem: gridItem,
      cellText: {
        textAlign: 'left',
      },
    }
  }
)
